import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { MultiSelect } from "../../components/MultiSelect";

import { artistsApi, eventsApi, portfolioApi } from "../../api";
import { QueryErrorBanner, KpiStripSkeleton } from "../../components/feedback/QueryFeedback";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { chartTooltipProps } from "../../constants/chartTheme";
import { useAuth } from "../../context/AuthContext";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { queryKeys } from "../../queryKeys";
import type {
  Artist,
  EventListItem,
  PortfolioMetricsBucket,
  PortfolioMetricsResponse,
  PortfolioTimeframe,
  PortfolioTransaction,
  PortfolioXAxisDimension,
  PortfolioYAxisMetric,
  UpcomingPayout,
} from "../../types";
import { formatINR, formatINRCompact } from "../../utils/format";
import { useFinanceChrome } from "./FinanceLayout";
import "./FinanceLayout.css";

const CHART_AXIS_TICK = { fill: "#b2b3c1", fontSize: 11 };
const PIE_COLORS = ["#06B6D4", "#22C55E", "#A855F7", "#F59E0B", "#E50914", "#38BDF8", "#FB7185", "#A3E635"];
const METRIC_STYLE: Record<string, { stroke: string; fill: string }> = {
  revenue: { stroke: "#38BDF8", fill: "rgba(56, 189, 248, 0.32)" },
  profit: { stroke: "#22C55E", fill: "rgba(34, 197, 94, 0.28)" },
  expense: { stroke: "#E50914", fill: "rgba(229, 9, 20, 0.28)" },
  rsvps: { stroke: "#A855F7", fill: "rgba(168, 85, 247, 0.28)" },
  tickets: { stroke: "#F59E0B", fill: "rgba(245, 158, 11, 0.28)" },
  tickets_walkin: { stroke: "#FB7185", fill: "rgba(251, 113, 133, 0.28)" },
  tickets_rsvp: { stroke: "#818CF8", fill: "rgba(129, 140, 248, 0.28)" },
  tickets_guest_list: { stroke: "#2DD4BF", fill: "rgba(45, 212, 191, 0.28)" },
  sell_through: { stroke: "#F472B6", fill: "rgba(244, 114, 182, 0.28)" },
  roi: { stroke: "#A3E635", fill: "rgba(163, 230, 53, 0.28)" },
};
const STACK_ORDER = ["expense", "profit", "revenue"];

const Y_AXIS_OPTIONS: { value: PortfolioYAxisMetric; label: string }[] = [
  { value: "revenue", label: "Revenue" },
  { value: "expense", label: "Expense" },
  { value: "profit", label: "Profit" },
  { value: "rsvps", label: "RSVPs" },
  { value: "tickets", label: "Tickets" },
  { value: "tickets_walkin", label: "Tickets — Walk-ins" },
  { value: "tickets_rsvp", label: "Tickets — RSVPs" },
  { value: "tickets_guest_list", label: "Tickets — Guest List" },
  { value: "sell_through", label: "Sell Through" },
  { value: "roi", label: "ROI" },
];

const X_AXIS_OPTIONS: { value: PortfolioXAxisDimension; label: string }[] = [
  { value: "event", label: "Event" },
  { value: "location", label: "Location(s)" },
  { value: "artist", label: "Artists" },
];

const TIMEFRAME_OPTIONS: { value: PortfolioTimeframe; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "6months", label: "6 Months" },
  { value: "year", label: "Year" },
];

const metricLabel = (metric: string): string => Y_AXIS_OPTIONS.find((o) => o.value === metric)?.label ?? metric;

function isPercentMetric(metric: string): boolean {
  return metric === "sell_through" || metric === "roi" || metric.includes("Sell Through") || metric.includes("ROI");
}

function formatDeltaPct(value: number | null): string {
  if (value === null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}% vs last`;
}

function formatChartValue(value: number, metric: string): string {
  if (isPercentMetric(metric)) return `${value.toFixed(1)}%`;
  if (metric === "rsvps" || metric.startsWith("tickets")) return String(Math.round(value));
  return formatINR(value);
}

function formatPeriodWindow(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  const fmt = (iso: string, withYear: boolean) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      ...(withYear ? { year: "numeric" } : {}),
    });
  return `${fmt(start, start.slice(0, 4) !== end.slice(0, 4))} – ${fmt(end, true)}`;
}

const FinancePortfolioPage: React.FC = () => {
  const { token } = useAuth();
  const workspace = APP_WORKSPACE;
  const { search, editingKpis } = useFinanceChrome();
  const [selectedY, setSelectedY] = useState<PortfolioYAxisMetric[]>(["revenue"]);
  const [selectedX, setSelectedX] = useState<PortfolioXAxisDimension>("event");
  const [timeframe, setTimeframe] = useState<PortfolioTimeframe>("month");
  // Whole periods back from today; the server anchors the window in IST.
  const [offset, setOffset] = useState(0);
  const [insightMetric, setInsightMetric] = useState<PortfolioYAxisMetric>("profit");
  const [insightRank, setInsightRank] = useState<"both" | "highest" | "lowest">("both");
  const [filterEvent, setFilterEvent] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterArtist, setFilterArtist] = useState("all");
  const [filterGenre, setFilterGenre] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const summaryQuery = useQuery({
    queryKey: ["portfolio-summary", timeframe, offset],
    queryFn: () => portfolioApi.summary(timeframe, token!, offset),
    enabled: Boolean(token),
  });

  const metricsQuery = useQuery<PortfolioMetricsResponse>({
    queryKey: ["portfolio-metrics", selectedY, selectedX, timeframe, offset],
    queryFn: () => portfolioApi.metrics({ y: selectedY, x: selectedX, timeframe, offset }, token!),
    enabled: Boolean(token) && selectedY.length > 0,
  });

  const insightsQuery = useQuery({
    queryKey: ["portfolio-insights", insightMetric],
    queryFn: () => portfolioApi.insights(insightMetric, token!),
    enabled: Boolean(token),
  });

  const payoutsQuery = useQuery<UpcomingPayout[]>({
    queryKey: ["portfolio-upcoming-payouts"],
    queryFn: () => portfolioApi.upcomingPayouts(token!),
    enabled: Boolean(token),
  });

  const transactionsQuery = useQuery<PortfolioTransaction[]>({
    queryKey: ["portfolio-transactions"],
    queryFn: () => portfolioApi.transactions(token!),
    enabled: Boolean(token),
  });

  const eventsQuery = useQuery({
    queryKey: queryKeys.events.list(workspace),
    queryFn: () => eventsApi.list(token!),
    enabled: Boolean(token),
  });

  const artistsQuery = useQuery({
    queryKey: queryKeys.artists.list(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });

  const events = useMemo<EventListItem[]>(
    () => (Array.isArray(eventsQuery.data) ? eventsQuery.data : []),
    [eventsQuery.data],
  );
  const artists = useMemo<Artist[]>(
    () => (Array.isArray(artistsQuery.data) ? artistsQuery.data : []),
    [artistsQuery.data],
  );

  const genreByArtistId = useMemo(() => {
    const map = new Map<string, string>();
    artists.forEach((a) => {
      if (a.genre) map.set(a.id, a.genre);
    });
    return map;
  }, [artists]);

  const locations = useMemo(
    () => Array.from(new Set(events.map((e) => e.city).filter(Boolean))).sort(),
    [events],
  );
  const statuses = useMemo(
    () => Array.from(new Set(events.map((e) => e.status).filter(Boolean))).sort(),
    [events],
  );
  const genres = useMemo(
    () => Array.from(new Set(artists.map((a) => a.genre).filter((g): g is string => Boolean(g)))).sort(),
    [artists],
  );

  const stacked = selectedY.length > 1;
  const primaryY = selectedY[0] ?? "revenue";

  const { chartData, seriesKeys } = useMemo(() => {
    const ordered = [...selectedY].sort((a, b) => {
      const ai = STACK_ORDER.indexOf(a);
      const bi = STACK_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    const keys = ordered.map((metric) => metricLabel(metric));
    const series: PortfolioMetricsBucket[] = metricsQuery.data?.series ?? [];
    const rows = series.map((bucket) => {
      const row: Record<string, string | number> = { bucket: bucket.label ?? bucket.bucket };
      ordered.forEach((metric) => {
        const label = metricLabel(metric);
        row[label] = Object.values(bucket.groups).reduce(
          (sum, metrics) => sum + Number(metrics[metric] ?? 0),
          0,
        );
      });
      return row;
    });
    return { chartData: rows, seriesKeys: keys };
  }, [metricsQuery.data, selectedY]);

  const pieData = useMemo(() => {
    const totals: Record<string, Record<string, number>> = metricsQuery.data?.totals ?? {};
    return Object.entries(totals)
      .map(([group, metrics]: [string, Record<string, number>]) => ({ name: group, value: metrics[primaryY] ?? 0 }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [metricsQuery.data, primaryY]);

  const summary = summaryQuery.data;
  const insights = insightsQuery.data;
  const payouts: UpcomingPayout[] = payoutsQuery.data ?? [];
  const q = search.trim().toLowerCase();

  const filteredTransactions = useMemo(() => {
    const list: PortfolioTransaction[] = transactionsQuery.data ?? [];
    return list.filter((t) => {
      if (q) {
        const hay = `${t.event_name} ${t.type} ${t.party ?? ""} ${t.status}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterEvent !== "all" && t.event_id !== filterEvent) return false;
      if (filterStatus !== "all" && t.status.toLowerCase() !== filterStatus.toLowerCase()) return false;
      const event = events.find((e) => e.id === t.event_id);
      if (filterLocation !== "all" && event?.city !== filterLocation) return false;
      if (filterArtist !== "all" && event?.artist_id !== filterArtist && event?.artist !== filterArtist) return false;
      if (filterGenre !== "all") {
        const genre = event?.artist_id ? genreByArtistId.get(event.artist_id) : undefined;
        if (genre !== filterGenre) return false;
      }
      if (filterFrom && new Date(t.date) < new Date(filterFrom)) return false;
      if (filterTo && new Date(t.date) > new Date(`${filterTo}T23:59:59`)) return false;
      return true;
    });
  }, [
    events,
    filterArtist,
    filterEvent,
    filterFrom,
    filterGenre,
    filterLocation,
    filterStatus,
    filterTo,
    genreByArtistId,
    q,
    transactionsQuery.data,
  ]);

  const xLabel = X_AXIS_OPTIONS.find((o) => o.value === selectedX)?.label ?? "group";
  const periodLabel = TIMEFRAME_OPTIONS.find((o) => o.value === timeframe)?.label ?? "Month";
  const windowLabel = formatPeriodWindow(metricsQuery.data?.period_start, metricsQuery.data?.period_end);
  const changeTimeframe = (next: PortfolioTimeframe) => {
    setTimeframe(next);
    setOffset(0);
  };

  return (
    <div className="finance-portfolio finance-portfolio--fit">
      <div className="finance-portfolio__screen">
      {summaryQuery.isError ? (
        <QueryErrorBanner message="Unable to load portfolio summary" onRetry={() => void summaryQuery.refetch()} retrying={summaryQuery.isFetching} />
      ) : null}

      <section className="finance-portfolio__top">
        {editingKpis ? (
          <div className="finance-portfolio__kpi-edit">
            <span className="eventos-subtle">Highlight period</span>
            <div className="finance-portfolio__timeframe-group">
              {TIMEFRAME_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`finance-layout__tab${timeframe === opt.value ? " finance-layout__tab--active" : ""}`}
                  onClick={() => changeTimeframe(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {summaryQuery.isPending ? (
          <KpiStripSkeleton count={7} />
        ) : summary ? (
          <div className="finance-portfolio__kpis">
            <article className="finance-portfolio__card finance-portfolio__card--primary">
              <div className="finance-portfolio__card-label">Total Revenue ({periodLabel})</div>
              <div className="finance-portfolio__card-value">{formatINR(summary.revenue.value ?? 0)}</div>
              <div className="finance-portfolio__card-sub">{formatDeltaPct(summary.revenue.delta_pct)}</div>
            </article>
            <article className="finance-portfolio__card">
              <div className="finance-portfolio__card-label">Expenses ({periodLabel})</div>
              <div className="finance-portfolio__card-value">{formatINR(summary.expense.value ?? 0)}</div>
              <div className="finance-portfolio__card-sub">{formatDeltaPct(summary.expense.delta_pct)}</div>
            </article>
            <article className="finance-portfolio__card">
              <div className="finance-portfolio__card-label">Pending Invoices</div>
              <div className="finance-portfolio__card-value">{formatINR(summary.pending_invoices.value ?? 0)}</div>
              <div className="finance-portfolio__card-sub">{formatDeltaPct(summary.pending_invoices.delta_pct)}</div>
            </article>
            <article className="finance-portfolio__card finance-portfolio__card--payouts">
              <div className="finance-portfolio__card-label">Upcoming Payouts</div>
              {payouts.length === 0 ? (
                <div className="finance-portfolio__card-sub">None due</div>
              ) : (
                <ul className="finance-portfolio__payout-mini">
                  {payouts.slice(0, 2).map((p) => (
                    <li key={p.invoice_id}>
                      <span>{p.event_name}</span>
                      <strong>{formatINRCompact(p.amount)}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </article>
            <article className="finance-portfolio__card">
              <div className="finance-portfolio__card-label">Total Profit</div>
              <div className={`finance-portfolio__card-value${(summary.profit.value ?? 0) >= 0 ? " is-up" : " is-down"}`}>
                {formatINR(summary.profit.value ?? 0)}
              </div>
              <div className="finance-portfolio__card-sub">{formatDeltaPct(summary.profit.delta_pct)}</div>
            </article>
            <article className="finance-portfolio__card">
              <div className="finance-portfolio__card-label">ROI</div>
              <div className="finance-portfolio__card-value">
                {summary.roi.value === null ? "—" : `${summary.roi.value.toFixed(1)}%`}
              </div>
              <div className="finance-portfolio__card-sub">{formatDeltaPct(summary.roi.delta_pct)}</div>
            </article>
            <article className="finance-portfolio__card">
              <div className="finance-portfolio__card-label">Avg Sell Through %</div>
              <div className="finance-portfolio__card-value">
                {summary.avg_sell_through.value === null ? "—" : `${summary.avg_sell_through.value.toFixed(1)}%`}
              </div>
              <div className="finance-portfolio__card-sub">Sold / Capacity</div>
            </article>
          </div>
        ) : (
          <div className="eventos-empty">No portfolio summary yet.</div>
        )}

        <aside className="eventos-card finance-portfolio__insights">
          <div className="finance-portfolio__insights-head">
            <div className="eventos-label">Insights</div>
            <div className="finance-portfolio__insights-filters">
              <MobileSelect
                label="Highest or lowest"
                className="eventos-select"
                value={insightRank}
                onChange={(v) => setInsightRank(v as "both" | "highest" | "lowest")}
                options={[
                  { value: "both", label: "Highest / Lowest" },
                  { value: "highest", label: "Highest" },
                  { value: "lowest", label: "Lowest" },
                ]}
              />
              <MobileSelect
                label="Rank by metric"
                className="eventos-select"
                value={insightMetric}
                onChange={(v) => setInsightMetric(v as PortfolioYAxisMetric)}
                options={Y_AXIS_OPTIONS.filter((o) => !o.value.startsWith("tickets_")).map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            </div>
          </div>
          {insights ? (
            <div className="finance-portfolio__insight-grid">
              {(["venue", "market", "artist"] as const).map((dim) => (
                <div key={dim} className="finance-portfolio__insight-block">
                  <div className="finance-portfolio__insight-title">
                    {dim === "venue" ? "Venue" : dim === "market" ? "Market" : "Artist"}
                  </div>
                  {insightRank !== "lowest" ? (
                    <div className="finance-portfolio__insight-row is-up">
                      Highest: {insights[dim].best?.name ?? "—"}
                    </div>
                  ) : null}
                  {insightRank !== "highest" ? (
                    <div className="finance-portfolio__insight-row is-down">
                      Lowest: {insights[dim].worst?.name ?? "—"}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="eventos-empty">Loading insights…</div>
          )}
        </aside>
      </section>

      <section className="finance-portfolio__mid finance-portfolio__mid--filters">
        <div className="eventos-card finance-portfolio__chart-card">
          <div className="finance-portfolio__chart-controls">
            <div className="finance-portfolio__chart-controls-start">
              <MultiSelect
                className="finance-portfolio__axis-select"
                options={Y_AXIS_OPTIONS}
                values={selectedY}
                onChange={(vals) => setSelectedY(vals as PortfolioYAxisMetric[])}
                placeholder="Revenue"
              />
              <MobileSelect
                label="X-axis dimension"
                className="eventos-select finance-portfolio__axis-select"
                value={selectedX}
                onChange={(v) => setSelectedX(v as PortfolioXAxisDimension)}
                options={X_AXIS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
              />
            </div>
            <MobileSelect
              label="Period"
              className="eventos-select finance-portfolio__axis-select"
              value={timeframe}
              onChange={(v) => changeTimeframe(v as PortfolioTimeframe)}
              options={TIMEFRAME_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
            <div className="finance-portfolio__period-nav" role="group" aria-label="Change period">
              <button
                type="button"
                className="finance-layout__tab"
                aria-label={`Previous ${periodLabel.toLowerCase()}`}
                onClick={() => setOffset((o) => o + 1)}
              >
                ‹
              </button>
              <span className="finance-portfolio__period-label" aria-live="polite">
                {windowLabel ?? periodLabel}
              </span>
              <button
                type="button"
                className="finance-layout__tab"
                aria-label={`Next ${periodLabel.toLowerCase()}`}
                onClick={() => setOffset((o) => o - 1)}
              >
                ›
              </button>
              {offset !== 0 ? (
                <button type="button" className="finance-layout__tab" onClick={() => setOffset(0)}>
                  Today
                </button>
              ) : null}
            </div>
          </div>

          {selectedY.length === 0 ? (
            <div className="eventos-empty">Select at least one Y-axis metric.</div>
          ) : chartData.length === 0 ? (
            <div className="eventos-empty">{metricsQuery.isPending ? "Loading…" : "No data for this selection."}</div>
          ) : (
            <div className="finance-portfolio__chart-frame">
              <span className="finance-portfolio__axis-label finance-portfolio__axis-label--y">Y-axis</span>
              <div className="finance-portfolio__chart">
                <ResponsiveContainer>
                  {stacked ? (
                    <AreaChart data={chartData} margin={{ top: 10, right: 72, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="bucket" tick={CHART_AXIS_TICK} />
                      <YAxis tickFormatter={formatINRCompact} tick={CHART_AXIS_TICK} width={64} />
                      <Tooltip
                        {...chartTooltipProps}
                        formatter={(value: number | string, name: string) => [
                          formatChartValue(Number(value) || 0, name),
                          name,
                        ]}
                      />
                      {seriesKeys.map((key) => {
                        const metric = Y_AXIS_OPTIONS.find((o) => o.label === key)?.value ?? "";
                        const style = METRIC_STYLE[metric] ?? { stroke: "#38BDF8", fill: "rgba(56, 189, 248, 0.28)" };
                        return (
                          <Area
                            key={key}
                            type="monotone"
                            dataKey={key}
                            name={key}
                            stackId="stack"
                            stroke={style.stroke}
                            fill={style.fill}
                            strokeWidth={2}
                          >
                            <LabelList
                              dataKey={key}
                              content={(props) => {
                                const x = typeof props.x === "number" ? props.x : Number(props.x);
                                const y = typeof props.y === "number" ? props.y : Number(props.y);
                                if (props.index !== chartData.length - 1 || !Number.isFinite(x) || !Number.isFinite(y)) {
                                  return null;
                                }
                                return (
                                  <text x={x + 8} y={y + 4} fill={style.stroke} fontSize={11} fontWeight={700}>
                                    {key}
                                  </text>
                                );
                              }}
                            />
                          </Area>
                        );
                      })}
                    </AreaChart>
                  ) : (
                    <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="bucket" tick={CHART_AXIS_TICK} />
                      <YAxis
                        tickFormatter={(v: number) =>
                          isPercentMetric(primaryY) || primaryY === "rsvps" || primaryY.startsWith("tickets")
                            ? String(v)
                            : formatINRCompact(v)
                        }
                        tick={CHART_AXIS_TICK}
                        width={64}
                      />
                      <Tooltip
                        {...chartTooltipProps}
                        formatter={(value: number | string, name: string) => [
                          formatChartValue(Number(value) || 0, primaryY),
                          name,
                        ]}
                      />
                      {seriesKeys.map((key) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key}
                          stroke="#E8E8F0"
                          strokeWidth={2.4}
                          dot={{ r: 3, fill: "#E8E8F0" }}
                          activeDot={{ r: 5 }}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
              <span className="finance-portfolio__axis-label finance-portfolio__axis-label--x">X-axis</span>
            </div>
          )}
        </div>

        <aside className="eventos-card finance-portfolio__filter-rail" aria-label="Chart filters">
            <div className="eventos-label">Filters</div>
            <label className="finance-portfolio__filter-group">
              <span className="eventos-subtle">Events</span>
              <MobileSelect
                label="Event"
                className="eventos-select"
                value={filterEvent}
                onChange={(v) => setFilterEvent(v)}
                options={[
                  { value: "all", label: "All events" },
                  ...events.map((event) => ({ value: event.id, label: event.name })),
                ]}
              />
            </label>
            <label className="finance-portfolio__filter-group">
              <span className="eventos-subtle">Location</span>
              <MobileSelect
                label="Location"
                className="eventos-select"
                value={filterLocation}
                onChange={(v) => setFilterLocation(v)}
                options={[
                  { value: "all", label: "All locations" },
                  ...locations.map((city) => ({ value: city, label: city })),
                ]}
              />
            </label>
            <label className="finance-portfolio__filter-group">
              <span className="eventos-subtle">Status</span>
              <MobileSelect
                label="Status"
                className="eventos-select"
                value={filterStatus}
                onChange={(v) => setFilterStatus(v)}
                options={[
                  { value: "all", label: "All statuses" },
                  ...statuses.map((status) => ({ value: status, label: status })),
                ]}
              />
            </label>
            <label className="finance-portfolio__filter-group">
              <span className="eventos-subtle">Date range</span>
              <input className="eventos-input" type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
              <input className="eventos-input" type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
            </label>
            <label className="finance-portfolio__filter-group">
              <span className="eventos-subtle">Artists</span>
              <MobileSelect
                label="Artist"
                className="eventos-select"
                value={filterArtist}
                onChange={(v) => setFilterArtist(v)}
                options={[
                  { value: "all", label: "All artists" },
                  ...artists.map((artist) => ({ value: artist.id, label: artist.stage_name || artist.name })),
                ]}
              />
            </label>
            <label className="finance-portfolio__filter-group">
              <span className="eventos-subtle">Genre</span>
              <MobileSelect
                label="Genre"
                className="eventos-select"
                value={filterGenre}
                onChange={(v) => setFilterGenre(v)}
                options={[
                  { value: "all", label: "All genres" },
                  ...genres.map((genre) => ({ value: genre, label: genre })),
                ]}
              />
            </label>
          </aside>
      </section>
      </div>

      <section className="finance-portfolio__bottom">
        <div className="eventos-card finance-portfolio__pie-card">
          <div className="eventos-label">
            {metricLabel(primaryY)} by {xLabel}
          </div>
          {pieData.length === 0 ? (
            <div className="eventos-empty">No data.</div>
          ) : (
            <div className="finance-portfolio__chart finance-portfolio__chart--sm">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="46%"
                    innerRadius={38}
                    outerRadius={62}
                    paddingAngle={2}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...chartTooltipProps}
                    formatter={(value: number | string, name: string) => [
                      formatChartValue(Number(value) || 0, primaryY),
                      name,
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="eventos-card finance-portfolio__tx-card">
          <div className="eventos-label">Transactions</div>
          {filteredTransactions.length === 0 ? (
            <div className="eventos-empty">{transactionsQuery.isPending ? "Loading…" : "No transactions yet."}</div>
          ) : (
            <div className="finance-portfolio__tx-wrap">
              {/* `--stack` + data-label lets the same markup render as columns on
                  desktop and as a labelled card per row on a phone, where six
                  columns would otherwise scroll the amount out of sight. */}
              <table className="app-shell-table app-shell-table--stack">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Party/Ref</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t) => (
                    <tr key={t.invoice_id}>
                      <td data-label="Event">{t.event_name}</td>
                      <td data-label="Date">{new Date(t.date).toLocaleDateString("en-IN")}</td>
                      <td data-label="Type">{t.type}</td>
                      <td data-label="Party/Ref">{t.party || "—"}</td>
                      <td data-label="Amount">{formatINR(t.amount)}</td>
                      <td data-label="Status">{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FinancePortfolioPage;
